/**
 * Tracker Chat Component
 *
 * AI chat interface for discussing relationship tracker entries.
 * Supports date range selection and streaming AI responses.
 * Includes error display, smart auto-scroll, and loading states.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { shouldSendOnEnter } from "../../lib/composerKeys";
import { format, subMonths } from "date-fns";
import {
  Send,
  HeartHandshake,
  User,
  Plus,
  Loader2,
  AlertCircle,
  RotateCcw,
  MessageCircle,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import { DateRangeSelector } from "./DateRangeSelector";
import {
  useRelationshipChat,
  useCreateRelationshipChat,
  useStreamingRelationshipChat,
} from "@/hooks/useRelationshipChat";
import { toast } from "sonner";

export function TrackerChat() {
  const today = format(new Date(), "yyyy-MM-dd");
  const oneMonthAgo = format(subMonths(new Date(), 1), "yyyy-MM-dd");

  const [startDate, setStartDate] = useState(oneMonthAgo);
  const [endDate, setEndDate] = useState(today);
  const [chatId, setChatId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(
    null
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isUserNearBottomRef = useRef(true);

  const { data: chat } = useRelationshipChat(chatId);
  const createChat = useCreateRelationshipChat();
  const { isStreaming, streamingContent, error, sendMessage } =
    useStreamingRelationshipChat(chatId);

  // Track scroll position — only auto-scroll if user is near the bottom
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isUserNearBottomRef.current = distanceFromBottom < 100;
  }, []);

  // Smart auto-scroll: only scroll if user hasn't scrolled up
  useEffect(() => {
    if (isUserNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chat?.messages, streamingContent]);

  const handleStartChat = async () => {
    if (startDate > endDate) {
      toast.error("Start date must be before end date");
      return;
    }

    try {
      const newChat = await createChat.mutateAsync({
        date_range_start: startDate,
        date_range_end: endDate,
      });
      setChatId(newChat.id);
    } catch {
      toast.error("Failed to create chat session");
    }
  };

  const handleNewChat = () => {
    setChatId(null);
    setInputValue("");
    setLastFailedMessage(null);
  };

  const handleSend = async () => {
    const content = inputValue.trim();
    if (!content || isStreaming) return;

    setInputValue("");
    setLastFailedMessage(content);
    isUserNearBottomRef.current = true;
    await sendMessage(content);
    // Only clear lastFailedMessage if no error occurred.
    // If there was an error, keep it so the retry button works.
  };

  // Clear the failed message when streaming succeeds (error becomes null
  // after a successful send, while lastFailedMessage is still set).
  useEffect(() => {
    if (!error && !isStreaming && lastFailedMessage) {
      setLastFailedMessage(null);
    }
  }, [error, isStreaming, lastFailedMessage]);

  const handleRetry = async () => {
    if (!lastFailedMessage || isStreaming) return;
    isUserNearBottomRef.current = true;
    await sendMessage(lastFailedMessage);
    setLastFailedMessage(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (shouldSendOnEnter(e)) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [inputValue]);

  const messages = chat?.messages || [];

  // Pre-chat state: compact card with date range + start button
  if (!chatId) {
    return (
      <div className="rounded-xl border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="rounded-lg p-2 bg-primary/10">
            <MessageCircle className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h3 className="font-medium text-sm">Discuss Your Entries</h3>
            <p className="text-xs text-muted-foreground">
              Select a time period and chat with AI about your patterns
            </p>
          </div>
        </div>

        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onRangeChange={(start, end) => {
            setStartDate(start);
            setEndDate(end);
          }}
        />

        <Button
          onClick={handleStartChat}
          disabled={createChat.isPending}
          className="w-full"
        >
          {createChat.isPending && (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          )}
          {createChat.isPending ? "Starting chat..." : "Start AI Discussion"}
        </Button>
      </div>
    );
  }

  // Active chat state: full chat interface in a card
  return (
    <div className="rounded-xl border bg-card flex flex-col overflow-hidden min-h-[500px]">
      {/* Chat header */}
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HeartHandshake className="h-4 w-4 text-primary" />
          <h3 className="font-medium text-sm">AI Discussion</h3>
        </div>
        <Button variant="outline" size="sm" onClick={handleNewChat}>
          <Plus className="h-4 w-4 mr-1" />
          New Chat
        </Button>
      </div>

      {/* Messages area */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {messages.length === 0 && !isStreaming && (
          <div className="text-center text-muted-foreground text-sm py-8 space-y-2">
            <HeartHandshake className="w-8 h-8 mx-auto opacity-40" />
            <p>
              Ask me anything about your relationship entries for this period.
            </p>
            <p className="text-xs">
              I can help you identify patterns, explore feelings, and gain
              perspective.
            </p>
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-3 ${
              msg.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            {msg.role === "assistant" && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <HeartHandshake className="w-4 h-4 text-primary" />
              </div>
            )}
            <div
              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                msg.role === "user"
                  ? "bg-primary text-primary-foreground rounded-tr-none"
                  : "bg-muted rounded-tl-none"
              }`}
            >
              {msg.role === "assistant" ? (
                <div className="prose prose-sm max-w-none dark:prose-invert break-words">
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap break-words">
                  {msg.content}
                </p>
              )}
            </div>
            {msg.role === "user" && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <User className="w-4 h-4 text-primary-foreground" />
              </div>
            )}
          </div>
        ))}

        {/* Streaming content */}
        {isStreaming && streamingContent && (
          <div className="flex gap-3 justify-start">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <HeartHandshake className="w-4 h-4 text-primary" />
            </div>
            <div className="max-w-[80%] rounded-lg px-4 py-2 bg-muted rounded-tl-none">
              <div className="prose prose-sm max-w-none dark:prose-invert break-words">
                <ReactMarkdown>{streamingContent}</ReactMarkdown>
                <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-0.5" />
              </div>
            </div>
          </div>
        )}
        {isStreaming && !streamingContent && (
          <div className="flex gap-3 justify-start">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <HeartHandshake className="w-4 h-4 text-primary" />
            </div>
            <div className="max-w-[80%] rounded-lg px-4 py-2 bg-muted rounded-tl-none">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce" />
                  <div className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce [animation-delay:0.2s]" />
                  <div className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce [animation-delay:0.4s]" />
                </div>
                <span className="text-xs text-muted-foreground">
                  Thinking...
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Error display with retry */}
        {error && !isStreaming && (
          <div className="flex gap-3 justify-start">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-destructive" />
            </div>
            <div className="max-w-[80%] rounded-lg px-4 py-3 bg-destructive/5 border border-destructive/20">
              <p className="text-sm text-destructive">
                Something went wrong. Please try again.
              </p>
              {lastFailedMessage && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetry}
                  className="mt-2"
                >
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              )}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your entries..."
            className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            rows={1}
            disabled={isStreaming}
          />
          <Button
            onClick={handleSend}
            disabled={!inputValue.trim() || isStreaming}
            size="icon"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground text-center mt-2">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
